let cachedUri: string | undefined

export const Util = {
  /**
   * Return the locale to use.
   */
  getLocale: (): string => {
    return 'en-US'
  },

  /**
   * Process a ceiling with 2 decimal.
   */
  ceilTo2Digits: (value: number): number => {
    return Math.ceil(value * 100) / 100
  },

  /**
   * Return the absolute value with rounding of the given number.
   *
   * @param value
   *  The consumption value, or undefined.
   * @returns The consumption value for display.
   */
  absRounding: (value: number | undefined): number => {
    return Number(Math.abs(value ?? 0).toFixed(1))
  },

  /**
   * Wait the processing during the given delay.
   * @param ms
   *    The duration of the waiting in number of milliseconds.
   * @returns
   *    The Promise which will complete after the sleep duration.
   *
   * To use the function, use `await` instruction:
   *
   * ```
   *  // Wait for 10 seconds before proceeding
   *  await sleep(10000);
   * ```
   */
  sleep: (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms))
  },

  getEnv() {
    if (typeof import.meta?.env !== 'undefined') {
      return import.meta.env
    }

    if (typeof process !== 'undefined' && process.env) {
      return process.env
    }

    throw new Error('env is not defined')
  },

  getApiBaseUri: (): string => {
    if (cachedUri) return cachedUri

    if (
      typeof import.meta !== 'undefined' &&
      import.meta.env?.VITE_API_BASE_URI
    ) {
      cachedUri = import.meta.env.VITE_API_BASE_URI
      return cachedUri ?? ''
    }

    if (typeof process !== 'undefined' && process.env?.VITE_API_BASE_URI) {
      cachedUri = process.env.VITE_API_BASE_URI
      return cachedUri ?? ''
    }

    throw new Error('VITE_API_BASE_URI is not defined')
  },

  /**
   * Returns true if the browser is Safari.
   */
  isSafari(): boolean {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  },

  /**
   * Check if the the viven string is null, undefined or empty
   */
  isNullOrEmpty: (input: string | null | undefined): boolean => {
    return input == null || input.trim() === ''
  },

  /**
   * Return true if the current date (now) is within the first month starting
   * from the date given as argument.
   * @param startDate The start date of the month.
   * @param now An optional date to set instead of now, for debug purpose.
   * @returns A boolean.
   *
   * To test the function with another date than now:
   *   isInFirstMonth(new Date('2025-04-30'), new Date('2025-04-15'))
   */
  isInFirstMonth: (startDate: Date, now = new Date()): boolean => {
    const oneMonthLater = new Date(startDate)
    oneMonthLater.setMonth(oneMonthLater.getMonth() + 1)
    return now >= startDate && now < oneMonthLater
  },

  /**
   * Return true if the current date (now) is within the month ending
   * with the date given as argument (inclusive).
   * @param endDate The end date of the month.
   * @param now An optional date to set instead of now, for debug purpose.
   * @returns A boolean.
   *
   * To test the function with another date than now:
   *   isInMonthEndingWith(new Date('2025-04-30'), new Date('2025-04-15'))
   */
  isInMonthEndingWith: (endDate: Date, now: Date = new Date()): boolean => {
    const start = new Date(endDate)
    start.setMonth(start.getMonth() - 1)
    start.setDate(start.getDate() + 1)

    // Zero out time for safe comparison (optional but common in billing)
    const nowMidnight = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    )
    const startMidnight = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate()
    )
    const endMidnight = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate()
    )

    return nowMidnight >= startMidnight && nowMidnight <= endMidnight
  },

  /**
   * Return true if the given date is equal to Epoc
   * (i.e. 1970-01-01T00:00:00.000Z).
   *
   * @param date The date.
   * @returns true if Epoch is given.
   */
  isEpoch: (date: Date): boolean => {
    return date.getTime() === 0
  },
}
